/** Client (dealer) endpoints (`/clients`). Management list/update + client self-service. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, patchData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import { ClientProfile } from "../types/models";
import { ListParams } from "../types/api";

export interface UpdateClientInput {
  companyName?: string;
  contactPerson?: string;
  phone?: string;
  address?: string;
  city?: string;
  taxNumber?: string;
  isActive?: boolean;
}

export type UpdateMyProfileInput = Omit<UpdateClientInput, "isActive">;

export const clientsApi = {
  list: (params?: ListParams) => getList<ClientProfile>("/clients", params),
  getById: (id: string) => getData<ClientProfile>(`/clients/${id}`),
  update: (id: string, input: UpdateClientInput) =>
    patchData<ClientProfile>(`/clients/${id}`, input),
  getMyProfile: () => getData<ClientProfile>("/clients/me/profile"),
  updateMyProfile: (input: UpdateMyProfileInput) =>
    patchData<ClientProfile>("/clients/me/profile", input),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useClients = (params?: ListParams) =>
  useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: () => clientsApi.list(params),
  });

export const useClient = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.clients.detail(id ?? ""),
    queryFn: () => clientsApi.getById(id as string),
    enabled: Boolean(id),
  });

export const useMyClientProfile = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.clients.myProfile,
    queryFn: () => clientsApi.getMyProfile(),
    enabled,
  });

export const useUpdateClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClientInput }) =>
      clientsApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.clients.all }),
  });
};

export const useUpdateMyProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMyProfileInput) => clientsApi.updateMyProfile(input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.clients.myProfile }),
  });
};
